import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Stars from "@/components/site/Stars";
import MaskedReveal from "@/components/site/MaskedReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function Success() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "checking", data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const sid = params.get("session_id");
    if (!sid) {
      setState({ phase: "error", error: "Missing session id.", data: null });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/checkout/status/${sid}`);
        const d = r.data;
        if (d.payment_status === "paid") {
          setState({ phase: "paid", data: d, error: null });
          return;
        }
        if (d.status === "expired") {
          setState({ phase: "expired", data: d, error: null });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setState({ phase: "timeout", data: d, error: null });
          return;
        }
        attempts.current += 1;
        setState({ phase: "pending", data: d, error: null });
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({
          phase: "error",
          data: null,
          error: e?.response?.data?.detail || "Could not verify payment.",
        });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [search]);

  const heading = {
    checking: ["Checking your", "orbit…"],
    pending: ["Payment is", "in orbit…"],
    paid: ["Welcome to", "the room."],
    expired: ["The door", "closed."],
    timeout: ["Still checking", "the signal…"],
    error: ["Signal lost."],
  }[state.phase];

  const label = {
    checking: "verifying",
    pending: "processing",
    paid: "confirmed",
    expired: "expired",
    timeout: "delayed",
    error: "error",
  }[state.phase];

  return (
    <main
      data-testid="success-page"
      className="relative min-h-screen bg-black text-white overflow-hidden flex flex-col justify-between"
    >
      {/* Cosmos */}
      <div aria-hidden className="absolute inset-0">
        <Stars density={1.4} drift={0.02} />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,#0e0e0e_0%,#050505_60%,#000_100%)]"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.06 }}
        transition={{ duration: 1.4 }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span className="x-mark text-[70vw] md:text-[46vw] leading-none">X</span>
      </motion.div>

      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
        <button
          onClick={() => navigate("/")}
          className="text-white/80 hover:text-white transition"
          data-testid="success-home"
        >
          ← R.I.V.H™
        </button>
        <span data-testid="success-status-label">{label}</span>
      </header>

      <section className="relative z-10 px-6 md:px-10 pb-24">
        <MaskedReveal
          as="h1"
          className="font-display text-[15vw] md:text-[10vw] uppercase leading-[0.85] tracking-[-0.04em] text-white"
          lines={heading}
        />

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-6">
            {state.phase === "paid" && state.data && (
              <div className="text-white/70 leading-relaxed">
                <p className="text-base md:text-lg">
                  Your order for{" "}
                  <span className="text-white">{state.data.item_name}</span> has
                  been received. You are in orbit.
                </p>
                <div className="mt-8 border-t border-white/15 pt-6 grid grid-cols-2 gap-6 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
                  <div>
                    <div className="text-white/40">Total</div>
                    <div className="text-white mt-1 text-lg tabular-nums">
                      ${(state.data.amount_total / 100).toFixed(2)}{" "}
                      {String(state.data.currency).toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <div className="text-white/40">Session</div>
                    <div className="text-white mt-1 break-all text-[10px]">
                      {String(state.data.session_id).slice(0, 22)}…
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(state.phase === "checking" || state.phase === "pending") && (
              <p className="text-white/60 leading-relaxed">
                Holding open the door. Do not close this window — we are
                confirming your signal with Stripe.
              </p>
            )}
            {state.phase === "timeout" && (
              <p className="text-white/60 leading-relaxed">
                Your payment is still processing. Check your email in a few
                minutes for confirmation.
              </p>
            )}
            {state.phase === "expired" && (
              <p className="text-white/60 leading-relaxed">
                The checkout session expired. Walk back into the showroom to
                try again.
              </p>
            )}
            {state.phase === "error" && (
              <p className="text-white/70 leading-relaxed">
                {state.error || "Something went wrong."}
              </p>
            )}
          </div>

          <div className="md:col-span-5 md:col-start-8 flex md:justify-end items-end">
            <button
              onClick={() => navigate("/")}
              className="btn-sharp"
              data-testid="success-cta"
            >
              <span>
                {state.phase === "paid"
                  ? "Walk back to the room →"
                  : "Return to Showroom →"}
              </span>
            </button>
          </div>
        </div>
      </section>

      <footer className="relative z-10 px-6 md:px-10 pb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 flex justify-between">
        <span>FIND YOUR PURPOSE</span>
        <span>R.I.V.H™</span>
      </footer>
    </main>
  );
}
